/**
 * Client-side auth helpers.
 * Keycloak OIDC runs server-side; this is the session state in the browser.
 */
import { getAuthToken, setAuthToken, getCurrentUser, setCurrentUser, type AuthUser } from "./tenant-context";
import { auditClientEvent } from "./audit-client";

const REFRESH_KEY = "dikdoc.auth.refresh";

export function isAuthenticated(): boolean {
  return getAuthToken() !== null;
}

export function logout(): void {
  const user = getCurrentUser();
  auditClientEvent({ action: "auth.logout", outcome: "success", metadata: { userId: user?.id } });
  setAuthToken(null);
  setCurrentUser(null);
  if (typeof window !== "undefined") {
    window.localStorage.removeItem(REFRESH_KEY);
    window.location.href = "/login";
  }
}

export interface LoginResult {
  user: AuthUser;
  token: string;
  refreshToken?: string;
  expiresIn: number;
}

export async function loginWithCredentials(email: string, password: string): Promise<LoginResult> {
  const res = await fetch("/api/auth/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) {
    auditClientEvent({ action: "auth.login", outcome: "failure", httpStatus: res.status });
    throw new Error("Identifiants invalides");
  }
  const data = (await res.json()) as LoginResult;
  setAuthToken(data.token);
  setCurrentUser(data.user);
  if (data.refreshToken) {
    window.localStorage.setItem(REFRESH_KEY, data.refreshToken);
  }
  auditClientEvent({ action: "auth.login", outcome: "success", metadata: { userId: data.user.id } });
  return data;
}

export async function refreshToken(): Promise<string | null> {
  const refresh = window.localStorage.getItem(REFRESH_KEY);
  if (!refresh) return null;
  const res = await fetch("/api/auth/refresh", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken: refresh }),
  });
  if (!res.ok) return null;
  const data = (await res.json()) as { token: string; refreshToken?: string };
  setAuthToken(data.token);
  if (data.refreshToken) window.localStorage.setItem(REFRESH_KEY, data.refreshToken);
  return data.token;
}

/**
 * Hook-friendly: check if MFA is required for current user.
 * The actual MFA challenge happens server-side via Keycloak.
 */
export async function requireMfaFor(action: string): Promise<boolean> {
  const user = getCurrentUser();
  if (!user) return true; // unauthenticated → require auth first
  const sensitiveActions = new Set([
    "prescription.sign",
    "lab.result.approve",
    "imaging.report.sign",
    "patient.record.delete",
    "billing.claim.submit",
    "settings.update",
  ]);
  return sensitiveActions.has(action);
}
